<script>
import currency from '../../utils/currency';
import { Line  } from 'vue-chartjs';
import { mapGetters } from 'vuex';
import { scale, alpha } from '../../utils/colors';
import moment from 'moment';
import fy_months from '../../utils/fy_months';
export default {
  extends: Line,
  props: {
    savingsReport: {
      type: Array,
      default: () => [],
    },
    yearEnding: {
      type: Number,
      default: 1970,
    },
    view: {
      type: String,
      default: 'realized',
      validator: value => ['realized', 'forecasted'].indexOf(value) >= 0
    },
    savingsTarget: {
      type: Number
    }
  },
  data: () => {
    return {};
  },
  mounted() {
    this.render()
    },
  watch:{
    datasets(){
      this.render()
    }
  },
  methods: {
    render(){
      this.renderChart({ datasets: this.datasets }, this.options);
    },
    formatCurrency(num) {
      if (!this.app_user) {
        return '0';
      }
      currency.locale(
        this.currencyCodeById(this.app_user.organization.currencyId)
      );
      return currency(num).format('$0a.[0]');
    },
    async goToProject(id) {
      if (!this.projects[id]) {
        const _id = this.project_options.find(d => d.shortId === id).value;
        await this.$store.dispatch('getProjectDetail', _id);
      }
      this.$router.push({ name: 'ProjectDetail', params: { id } });
      document.querySelector('#main-view').scrollTo(0, 0);
    },
  },
  computed: {
    ...mapGetters(['currencyCodeById']),
    app_user() {
      return this.$store.getters.app_user;
    },
    projects() {
      return this.$store.getters.projects;
    },
    all_projects() {
      return this.$store.getters.all_projects;
    },
    project_options() {
      return Object.keys(this.all_projects)
        .filter(key => key !== this.project_id)
        .map(key => {
          return {
            text: this.all_projects[key].name,
            value: this.all_projects[key].id,
            shortId: this.all_projects[key].shortId,
          };
        });
    },
    fYear() {
      return fy_months(this.$store.getters.org.fy_month, this.yearEnding);
    },
    chartStart() {
      const fyStartYear = moment(`${this.fYear.fy_month} ${this.yearEnding}`, 'MMM YYYY').isAfter(moment(`${this.fYear.prev_month} ${this.yearEnding}`, 'MMM YYYY'))
        ? this.yearEnding - 1
        : this.yearEnding
      return moment(`${this.fYear.fy_month} ${fyStartYear}`, 'MMM YYYY').startOf(
        'month'
      );
    },
    chartEnd() {
      return moment(`${this.fYear.prev_month} ${this.yearEnding}`, 'MMM YYYY').endOf(
        'month'
      );
    },
    options() {
      currency.locale(
        this.app_user
          ? this.currencyCodeById(this.app_user.organization.currencyId)
          : ''
      );
      return {
        title: {
          display: true,
          text: 'Savings Report',
        },
        elements: {
          line: {
            tension: 0, // Bezier curve tension
          },
        },
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              type: 'time',
              time: {
                unit: 'month',
                // displayFormats: {
                //   month: 'MMM YY',
                // },
              },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 10,
                min: this.chartStart,
                max: this.chartEnd,
              },
            },
          ],
          yAxes: [
            {
              scaleLabel: {
                display: false,
                labelString: 'Cumulative value',
              },
              ticks: {
                // Include a currency sign in the ticks
                callback: function(value) {
                  return currency(value).format('$0a.[0]');
                },
                min: 0
              },
            },
          ],
        },
        tooltips: {
          filter: (tooltipItem, data) => {
            const { datasetIndex, index } = tooltipItem;
            const { tooltipHidden } =
              data.datasets &&
              data.datasets[datasetIndex] &&
              data.datasets[datasetIndex].data &&
              data.datasets[datasetIndex].data[index];
            return !tooltipHidden;
          },
          callbacks: {
            beforeTitle: (tooltipItems, data) => {
              let title = '';
              let labels = data.labels;
              let labelCount = labels ? labels.length : 0;

              if (tooltipItems.length > 0) {
                let item = tooltipItems[0];
                if (item.label) {
                  title = moment(item.label).format('D MMM YY');
                } else if (item.xLabel) {
                  title = moment(item.xLabel).format('D MMM YY');
                } else if (labelCount > 0 && item.index < labelCount) {
                  title = labels[item.index];
                }
              }

              return title;
            },
            title: function(tooltipItems, data) {
              let beforeTitle = '';

              if (tooltipItems.length > 0) {
                const { datasetIndex, index } = tooltipItems[0];
                const { projectName } =
                  data.datasets &&
                  data.datasets[datasetIndex] &&
                  data.datasets[datasetIndex].data &&
                  data.datasets[datasetIndex].data[index];
                beforeTitle = projectName || '';
              }

              return beforeTitle;
            },
            label: (tooltipItem, data) => {
              let toolTipLabel = data.datasets[tooltipItem.datasetIndex].label || '';

              if (toolTipLabel) {
                toolTipLabel += ': ';
              }

              const { datasetIndex, index } = tooltipItem;
              const { label } =
                data.datasets &&
                data.datasets[datasetIndex] &&
                data.datasets[datasetIndex].data &&
                data.datasets[datasetIndex].data[index];
              toolTipLabel += label ? currency(label).format('$0a.[0]') : '';
              return label ? toolTipLabel : null;
            },
            afterLabel: (tooltipItem, data) => {
              const { datasetIndex, index } = tooltipItem;
              const { afterLabel } =
                data.datasets &&
                data.datasets[datasetIndex] &&
                data.datasets[datasetIndex].data &&
                data.datasets[datasetIndex].data[index];
              return afterLabel || null;
            },
          },
        },
        onClick: e => {
          const chart = this.$data._chart;
          const { data } = chart;
          const activePoints = chart.getElementsAtEventForMode(
            e,
            'nearest',
            {
              intersect: true,
            },
            false
          );

          if (!activePoints.length > 0) return;
          const [{ _datasetIndex, _index }] = activePoints;
          const { shortId } =
            data.datasets &&
            data.datasets[_datasetIndex] &&
            data.datasets[_datasetIndex].data &&
            data.datasets[_datasetIndex].data[_index];

          if (!shortId) return;
          this.goToProject(shortId);
        },
      };
    },
    datasets() {
      // stringify/parse is a workaround, ChartJs internally mutates data and causes an infinite loop in our watcher
      const { realized, forecasted, estimated, target } = JSON.parse(JSON.stringify(this.data));
      const colors = alpha(scale(1), 0.6);

      switch (this.view) {
        case 'realized':
        return [
          {
            label: 'Realised Benefits',
            backgroundColor: colors[0],
            data: realized,
          },
          {
            label: 'FY Estimation',
            data: estimated,
            fill: false,
            borderDash: [10, 5],
            borderColor: '#595959',
            borderWidth: 1,
          },
          {
            label: 'Target',
            data: target,
            fill: false,
            borderDash: [10, 5],
            borderColor: '#fcba03',
            borderWidth: 1,
          },
        ];
        case 'forecasted':
          return [
            {
              label: 'Unrealised Forecasted Benefits',
              data: forecasted,
              borderColor: '#a9a9a9',
              borderWidth: 2,
            },
          ]
        default:
          return []
      }
    },
    data() {
      let realized = [];
      let forecasted = [];
      let estimated = [];
      let target = [];

      // Filter all benefits
      this.savingsReport
        .filter(
          benefit =>
            (
              !!benefit.forecastedDate
              && moment(benefit.forecastedDate).isBetween(this.chartStart, this.chartEnd, undefined, '[]')
              && !!benefit.forecastedAmountOrgCurrency
            ) ||
            (
              !!benefit.realizedDate
              && moment(benefit.realizedDate).isBetween(this.chartStart, this.chartEnd, undefined, '[]')
              && !!benefit.amountOrgCurrency
            )
        )
        .forEach(benefit => {
          if (benefit.forecastedDate && !benefit.realizedDate) forecasted.push(benefit);
          if (benefit.realizedDate) {
            realized.push(benefit);
          } else {
            estimated.push(benefit);
          }
        });

      let cumForecastedAmountOrgCurrency = 0;
      forecasted = forecasted
        .sort((a, b) =>
          new Date(a.forecastedDate) > new Date(b.forecastedDate) ? 1 : -1
        )
        .map(benefit => {
          cumForecastedAmountOrgCurrency += Number(
            benefit.forecastedAmountOrgCurrency
          );
          return {
            x: new Date(benefit.forecastedDate),
            y: cumForecastedAmountOrgCurrency,
            shortId: benefit.shortId,
            projectName: benefit.name,
            label: Number(benefit.forecastedAmountOrgCurrency)
          };
        });

      // Get realized data
      let cumAmountOrgCurrency = 0;
      realized = realized
        .sort((a, b) =>
          new Date(a.realizedDate) > new Date(b.realizedDate) ? 1 : -1
        )
        .map(benefit => {
          cumAmountOrgCurrency += Number(benefit.amountOrgCurrency);
          return {
            x: moment(benefit.realizedDate),
            y: cumAmountOrgCurrency,
            shortId: benefit.shortId,
            projectName: benefit.name,
            label: Number(benefit.amountOrgCurrency),
            afterLabel: 'Year to date: ' + currency(cumAmountOrgCurrency).format('$0a.[0]')
          };
        });

      // Get estimation data
      const outstandingForecastedAmounts = estimated.reduce(
        (total, benefit) => total + Number(benefit.forecastedAmountOrgCurrency),
        0
      );
      const estimatedStartingPoint = realized.slice(-1).pop();
      estimated.push({ ...estimatedStartingPoint, tooltipHidden: true });
      estimated.push({
        x: this.chartEnd,
        y: outstandingForecastedAmounts + cumAmountOrgCurrency,
        label: outstandingForecastedAmounts + cumAmountOrgCurrency,
        afterLabel: 'Forecasted amount still outstanding: ' + currency(outstandingForecastedAmounts).format('$0a.[0]')
      });

      // Get target data
      if (this.savingsTarget) {
        target.push({
          x: this.chartStart,
          y: this.savingsTarget,
          label: this.savingsTarget,
        });
        target.push({
          x: this.chartEnd,
          y: this.savingsTarget,
          label: this.savingsTarget,
        });
      }

      realized.unshift({
        x: moment(this.chartStart),
        y: 0,
        afterLabel: 'Financial year start'
      })

      forecasted.unshift({
        x: moment(this.chartStart),
        y: 0,
        afterLabel: 'Financial year start'
      })

      return { realized, forecasted, estimated, target };
    },
  },
};
</script>

<style lang="scss" scoped>
canvas#race{position:relative; z-index:1;}
</style>
